import { memo, useCallback, useState } from "react";
import { motion } from "framer-motion";
import { FaStar } from "react-icons/fa";
import type { RadioStation } from "./types";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";
import clickSound from "../../assets/audio/click.wav";

const PARTICLES = Array.from({ length: 8 }, (_, i) => ({
  id: i,
  angle: (i / 8) * 360,
}));

export const rowVariants = {
  hidden: { opacity: 0, x: -18 },
  show: {
    opacity: 1,
    x: 0,
    transition: { type: "spring" as const, stiffness: 280, damping: 22 },
  },
};

interface Props {
  station: RadioStation;
  isActive: boolean;
  isFavorite: boolean;
  onSelect: (id: string) => void;
  onFavoriteToggle: (id: string) => void;
}

const RadioStationRow = memo<Props>(
  ({ station, isActive, isFavorite, onSelect, onFavoriteToggle }) => {
    const { playEffect } = useAudioEffects();
    const [burstKey, setBurstKey] = useState(0);

    const handleFavorite = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setBurstKey((k) => k + 1);
        onFavoriteToggle(station.id);
        playEffect(clickSound, { volume: 0.5 });
      },
      [station.id, onFavoriteToggle, playEffect],
    );

    return (
      <motion.div
        variants={rowVariants}
        role="button"
        tabIndex={0}
        onClick={() => onSelect(station.id)}
        onKeyDown={(e) => e.key === "Enter" && onSelect(station.id)}
        className={`w-full flex items-center gap-3 px-4 py-[11px] cursor-pointer transition-colors border-[1px]
        ${
          isActive
            ? "bg-[#137FE3]/20 border-[#137FE3]"
            : "border-white/0 hover:bg-white/[0.03] bg-white/[0.01]"
        }`}
      >
        <img
          src={station.thumbnail}
          alt={station.name}
          draggable={false}
          className="w-[38px] h-[38px] rounded-md object-cover flex-shrink-0 select-none"
        />

        <div className="flex-1 min-w-0">
          <p className="text-white text-[15px] leading-tight">{station.name}</p>
          <p className="text-white/50 text-[13px] mt-[2px]">{station.type}</p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {isActive ? (
            <span className="text-[14px] font-[400] px-2 py-[2px] rounded-sm bg-[#137FE3]/50 border-[1px] border-[#137FE3] text-white">
              {station.frequency}
            </span>
          ) : (
            <span className="text-[14px] font-[400] px-2 py-[2px] rounded-sm bg-white/[0.04] text-white">
              {station.frequency}
            </span>
          )}

          {/* Favorite button with sparkle burst */}
          <div className="relative flex items-center justify-center">
            <motion.button
              onClick={handleFavorite}
              whileTap={{ scale: 0.75 }}
              className={`relative z-10 transition-colors flex-shrink-0 p-2 ${
                isFavorite
                  ? "text-[#137FE3] bg-[#137FE3]/10"
                  : "text-white/50 bg-white/10 hover:text-white/60"
              }`}
            >
              <motion.div
                animate={
                  isFavorite
                    ? { scale: [1, 1.6, 0.8, 1.2, 1], rotate: [0, -25, 25, -10, 0] }
                    : { scale: 1, rotate: 0 }
                }
                transition={{ duration: 0.45, ease: "easeInOut" }}
              >
                <FaStar size={16} />
              </motion.div>
            </motion.button>

            {/* Sparkle particles — keyed to burstKey so they remount on every click */}
            <div className="absolute inset-0 pointer-events-none overflow-visible">
              {burstKey > 0 &&
                PARTICLES.map(({ id, angle }) => (
                  <motion.div
                    key={`${burstKey}-${id}`}
                    className="absolute left-1/2 top-1/2 rounded-full pointer-events-none"
                    style={{
                      width: id % 2 === 0 ? 5 : 4,
                      height: id % 2 === 0 ? 5 : 4,
                      marginLeft: id % 2 === 0 ? -2.5 : -2,
                      marginTop: id % 2 === 0 ? -2.5 : -2,
                      backgroundColor:
                        id % 3 === 0
                          ? "#137FE3"
                          : id % 3 === 1
                            ? "#ffffff"
                            : "#64b5f6",
                    }}
                    initial={{ opacity: 1, x: 0, y: 0, scale: 0.4 }}
                    animate={{
                      opacity: 0,
                      scale: 1.2,
                      x: Math.cos((angle * Math.PI) / 180) * 26,
                      y: Math.sin((angle * Math.PI) / 180) * 26,
                    }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                  />
                ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  },
);

RadioStationRow.displayName = "RadioStationRow";
export default RadioStationRow;
